import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { PageHeader, StatTiles } from '@/components/PageHeader'
import { fetchStructure, type Structure, type StructureNode } from '@/features/organization/api'
import { PersonPanel } from '@/features/organization/PersonPanel'
import { fetchPeople, type Person } from '@/features/people/api'
import { usePortfolio } from '@/features/portfolio/usePortfolio'
import type { ApiError } from '@/lib/api/client'
import type { MembershipKind, Role } from '@/types/domain'

/**
 * Organization module — who exists, where they sit, and what that entitles them
 * to. See docs/03-product/modules/organization.md.
 *
 * The structure below comes from the API. It used to come from a JSON file
 * compiled into the bundle, which meant the group's shape was readable by
 * anyone who could load the page.
 */
const ROLE_MATRIX: { role: Role; kind: MembershipKind; scope: string }[] = [
  { role: 'owner', kind: 'employee', scope: 'scopeOrganization' },
  { role: 'division_lead', kind: 'employee', scope: 'scopeDivision' },
  { role: 'project_manager', kind: 'employee', scope: 'scopeProject' },
  { role: 'contributor', kind: 'employee', scope: 'scopeProject' },
  { role: 'collaborator', kind: 'collaborator', scope: 'scopeNamedProjects' },
  { role: 'guest', kind: 'guest', scope: 'scopeSharedClientVisible' },
]

const ROLE_KEY: Record<Role, string> = {
  owner: 'roleOwner',
  division_lead: 'roleDivisionLead',
  project_manager: 'roleProjectManager',
  contributor: 'roleContributor',
  collaborator: 'roleCollaborator',
  guest: 'roleGuest',
}

const KIND_KEY: Record<MembershipKind, string> = {
  employee: 'kindEmployee',
  collaborator: 'kindCollaborator',
  guest: 'kindGuest',
}

const KIND_CLASS: Record<MembershipKind, string> = {
  employee: 'border-brand-300 bg-brand-50 text-brand-700',
  collaborator: 'border-[color:var(--color-accent-500)] text-[color:var(--color-accent-600)]',
  guest: 'border-line text-ink-500',
}

export function OrganizationPage() {
  const { t } = useTranslation()
  const [openPerson, setOpenPerson] = useState<string | null>(null)

  const structure = useQuery<Structure, ApiError>({
    queryKey: ['structure'],
    queryFn: fetchStructure,
    retry: false,
  })

  const people = useQuery<Person[], ApiError>({
    queryKey: ['people'],
    queryFn: fetchPeople,
    retry: false,
  })
  const { divisions, summary, isPending, isError } = usePortfolio()

  if (isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  return (
    <div>
      <PageHeader title={t('organization.title')} subtitle={t('organization.subtitle')} />

      {isError ? (
        <p className="text-ink-700 mb-6 text-sm">{t('organization.unreachable')}</p>
      ) : null}

      <StatTiles
        items={[
          { label: t('portfolio.divisions'), value: summary.divisions },
          { label: t('portfolio.projects'), value: summary.projects },
          { label: t('portfolio.clients'), value: summary.clients },
          ...(summary.people === null
            ? []
            : [{ label: t('portfolio.people'), value: summary.people }]),
        ]}
      />

      <section className="mb-9">
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">
          {structure.data === undefined ? t('organization.structure') : t('organization.structureTree')}
        </h2>
        <ul className="border-line bg-surface divide-line divide-y rounded-md border">
          {(structure.data?.divisions ?? divisions).map((division) => (
            <li key={division.id} className="flex flex-col gap-1.5 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-ink-500 w-12 shrink-0 font-mono text-[10px] tracking-widest uppercase">
                  {division.key}
                </span>
                <span className="text-ink-900 min-w-0 flex-1 truncate text-sm">{division.name}</span>
                {'lead' in division && division.lead ? (
                  <span className="text-ink-500 text-xs">{division.lead}</span>
                ) : null}
                <span className="text-ink-700 font-mono text-xs tabular-nums">
                  {division.projectCount}
                </span>
              </div>

              {'teams' in division && (division.teams ?? []).length > 0 ? (
                <ul className="flex flex-wrap gap-1.5 ps-16">
                  {(division.teams ?? []).map((team: StructureNode) => (
                    <li
                      key={team.id}
                      className="border-line text-ink-700 rounded-full border px-2 py-0.5 text-[11px]"
                    >
                      {team.name}
                      {team.headcount === undefined ? null : (
                        <span className="text-ink-500 ms-1 font-mono tabular-nums">{team.headcount}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-9">
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('portfolio.people')}</h2>
        {people.data === undefined ? (
          <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
        ) : (
          <ul className="border-line bg-surface divide-line divide-y rounded-md border">
            {people.data.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onClick={() => setOpenPerson(person.id)}
                  className="hover:bg-surface-2 flex w-full items-center gap-3 px-4 py-2.5 text-start"
                >
                  <Avatar name={person.name} color={person.color} size="sm" />
                  <span className="text-ink-900 min-w-0 flex-1 truncate text-sm">{person.name}</span>
                  <span className="text-ink-500 truncate text-xs">{person.jobTitle ?? '—'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">
          {t('organization.rolesAndReach')}
        </h2>
        <div className="border-line bg-surface overflow-x-auto rounded-md border">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
                <th className="px-4 py-2.5 text-start font-medium">{t('organization.role')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('organization.kind')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('organization.scope')}</th>
              </tr>
            </thead>
            <tbody>
              {ROLE_MATRIX.map((row) => (
                <tr key={row.role} className="border-line border-b last:border-0">
                  <td className="text-ink-900 px-4 py-2.5">
                    {t(`organization.${ROLE_KEY[row.role]}`)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`rounded border px-2 py-0.5 font-mono text-xs ${KIND_CLASS[row.kind]}`}
                    >
                      {t(`organization.${KIND_KEY[row.kind]}`)}
                    </span>
                  </td>
                  <td className="text-ink-700 px-4 py-2.5">
                    {t(`organization.${row.scope}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {openPerson === null ? null : (
        <PersonPanel key={openPerson} id={openPerson} onClose={() => setOpenPerson(null)} />
      )}
    </div>
  )
}
